import { dashboardRouteSearchSchema } from '../../dashboard/dashboardRouteSearch';
import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch, useLocation } from '@tanstack/react-router';

import { defaultBrandsFilter, type BrandsFilter } from './model';
import {
  parseBrandsRouteSearch,
  serializeBrandsRouteSearch,
  type BrandsRouteFilters,
  type BrandsRouteSearch,
} from './brandsRouteSearch';

export function useBrandsRouteFilters() {
  const scope = useAgencyCampaignContext();
  const pathname = useLocation({ select: (location) => location.pathname });
  const navigate = useNavigate({ from: brandsFilterRoute(pathname, Boolean(scope)) });
  const search = useSearch({ strict: false });
  const filters = useMemo(() => parseBrandsRouteSearch(search), [search]);
  const queryFilter: BrandsFilter = { ...defaultBrandsFilter, ...filters };

  const updateFilters = useCallback(
    (updater: (current: BrandsRouteFilters) => BrandsRouteFilters) => {
      void navigate({
        search: (current: BrandsRouteSearch) => ({
          ...dashboardRouteSearchSchema(current),
          ...serializeBrandsRouteSearch(updater(parseBrandsRouteSearch(current))),
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return {
    setAgencies: (agencyIds: string[]) => {
      updateFilters((current) => ({ ...current, agencyIds, page: 0 }));
    },
    filters,
    queryFilter,
    setSearch: (name: string) => {
      updateFilters((current) => ({ ...current, name, page: defaultBrandsFilter.page }));
    },
    setIndustries: (industries: string[]) => {
      updateFilters((current) => ({
        ...current,
        industries,
        page: defaultBrandsFilter.page,
      }));
    },
    setPagination: (page: number, size: number) => {
      updateFilters((current) => ({ ...current, page, size }));
    },
  };
}

function brandsFilterRoute(pathname: string, scoped: boolean) {
  if (pathname.startsWith('/campaigns/agencies/'))
    return '/campaigns/agencies/$agencyId/details' as const;
  if (pathname.endsWith('/campaigns/overview')) return '/a/$agencyId/campaigns/overview' as const;
  return scoped ? ('/a/$agencyId/campaigns/$section' as const) : ('/campaigns/brands' as const);
}
